<script setup lang="ts">
import ContactAddress from "@/components/ContactAddress.vue"
import VbEdit from "@/components/crud/VbEdit.vue"
import VbSaveButton from "@/components/crud/VbSaveButton.vue"
import type { DebitorProfile } from "@/model/app/debitor-profile"
import { type User } from "@/model/app/user"
import { normalizeModuleConfiguration, useHttpGet, VfFormErrors, VfFormLabel, VfFormRow, VfFormWidget } from "@/vf"
import AnimateIf from "@/vf/components/AnimateIf.vue"
import { computed, ref, type Ref } from "vue"

const emit = defineEmits<{
    (e: "saved"): void
    (e: "loaded", profile: User): void
}>()

const props = withDefaults(
    defineProps<{
        editMode?: boolean
        showCancel?: boolean
        editable?: boolean
        account?: string
    }>(),
    {
        editMode: false,
        showCancel: true,
        editable: true,
    },
)

let profile: Ref<User | undefined>
let refresh: () => Promise<void>

if (props.account) {
    const result = await useHttpGet<DebitorProfile>(`/user/${props.account}/debitor-profile`)
    profile = computed(() =>
        result.data.value !== undefined ? ({ primaryDebitorProfile: result.data.value } as User) : undefined,
    )
    refresh = result.refresh
} else {
    const result = await useHttpGet<User>("/my/profile")
    profile = result.data
    refresh = result.refresh
}

if (profile.value) {
    emit("loaded", profile.value)
}

const apiBase = props.account ? `/user/${props.account}/debitor-profile` : "/my/debitor-profile"

const config = normalizeModuleConfiguration({
    apiBase: () => apiBase,
    entityPath: () => apiBase,
    afterSaveAction: async () => {
        await refresh()
        emit("saved")
        emit("loaded", profile.value!)

        if (!props.editMode) {
            // dont switch back to view mode if edit mode was forced by the parent
            editMode.value = false
        }
    },
})

const editMode = ref(props.editMode)

function onSave(event: { cancel: boolean; entity: any }) {
    if (event.entity.copyFromContact && !event.entity.existingContact) {
        event.cancel = true
    }
}
</script>

<template>
    <div>
        <div v-animate-show="!editMode">
            <ContactAddress
                v-if="profile?.primaryDebitorProfile?.zip"
                :address="profile?.primaryDebitorProfile"
                class="mb-3"
            ></ContactAddress>
            <div v-else class="py-5 text-center text-muted">
                {{ $t("@app:my.debitor_profile.not_created_yet") }}
            </div>
            <button
                type="button"
                class="btn btn-primary btn-block btn-sm-inline-block"
                @click="editMode = true"
                v-if="props.editable"
            >
                {{
                    profile?.primaryDebitorProfile
                        ? $t("@app:my.debitor_profile.edit")
                        : $t("@app:my.debitor_profile.create")
                }}
            </button>
        </div>

        <AnimateIf :if="editMode">
            <VbEdit
                :item="profile?.primaryDebitorProfile"
                :config="config"
                :header="false"
                :toolbar="false"
                :schema-url="`${apiBase}/schema`"
                card-body-class="bg-transparent p-1"
                card-class="bg-transparent form-light"
                @save="onSave"
            >
                <template #default="{ formModel, schema }">
                    <template v-if="schema.filter(i => i.name === 'copyFromContact').length > 0">
                        <VfFormRow name="copyFromContact"></VfFormRow>

                        <div v-animate-show="formModel.copyFromContact">
                            <VfFormRow name="existingContact"></VfFormRow>
                        </div>
                    </template>

                    <div v-animate-show="!formModel.copyFromContact" style="--col-form-label-width: 100px">
                        <div class="row">
                            <div class="col-md-6">
                                <VfFormRow name="country"></VfFormRow>
                            </div>
                            <div class="col-md-6">
                                <VfFormRow name="addressLine2"></VfFormRow>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-md-6">
                                <VfFormRow name="street"></VfFormRow>
                            </div>
                            <div class="col-md-6">
                                <VfFormRow name="streetNumber"></VfFormRow>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-md-6">
                                <div class="form-group flex-column">
                                    <div class="flex-grow-1 align-items-center d-sm-flex">
                                        <VfFormLabel name="zip" class="col-form-label"></VfFormLabel>
                                        <div class="flex-grow-1">
                                            <VfFormWidget name="zip"></VfFormWidget>
                                        </div>
                                        <!--                                        <VarsityHelpBubble always class="ml-3">-->
                                        <!--                                            <VarsityContentProcessor-->
                                        <!--                                                :content="$t('@app:account_contact.zip_help.before')"-->
                                        <!--                                            ></VarsityContentProcessor>-->
                                        <!--                                            <ul>-->
                                        <!--                                                <li v-for="f of zipFormats[formModel.country]">{{ f }}</li>-->
                                        <!--                                            </ul>-->
                                        <!--                                            <VarsityContentProcessor-->
                                        <!--                                                :content="$t('@app:account_contact.zip_help.after')"-->
                                        <!--                                            ></VarsityContentProcessor>-->
                                        <!--                                        </VarsityHelpBubble>-->
                                    </div>
                                    <VfFormErrors name="zip"></VfFormErrors>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <VfFormRow name="city"></VfFormRow>
                            </div>
                        </div>
                    </div>

                    <div class="d-flex justify-content-between">
                        <button type="button" class="btn btn-primary" @click="editMode = false" v-if="props.showCancel">
                            {{ $t("crud:button.abort") }}
                        </button>

                        <VbSaveButton class="btn-block btn-sm-inline-block"></VbSaveButton>
                    </div>
                </template>
            </VbEdit>
        </AnimateIf>
    </div>
</template>
